/**
 * Format patient data blocks for insertion into letter provider notes.
 * Used by LetterInsertToolbar to insert pre-formatted snippets at cursor.
 */

import type { Patient } from '../types/patient'
import type { VitalSigns } from '../types/vitalSigns'
import { formatPatientName } from './name'
import { formatDate } from './locale'

export type LetterBlockType =
  | 'patient-details'
  | 'allergies'
  | 'current-medications'
  | 'chronic-conditions'
  | 'medical-history'
  | 'address'
  | 'contact'
  | 'latest-vitals'

export function toArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? v : (v as { name?: string }).name || String(v))).filter(Boolean)
  }
  if (value && typeof value === 'string') {
    return value.split(',').map((s) => s.trim()).filter(Boolean)
  }
  return []
}

function formatAddress(patient: Patient): string {
  const addr = patient.address
  if (!addr) return ''
  return [addr.street, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean).join(', ')
}

export function formatPatientBlockForLetter(
  patient: Patient | null | undefined,
  blockType: LetterBlockType,
  vitals?: VitalSigns[] | null
): string {
  if (!patient) return ''

  switch (blockType) {
    case 'patient-details': {
      const name = formatPatientName(patient)
      const dob = patient.dateOfBirth ? formatDate(patient.dateOfBirth) : ''
      const addr = formatAddress(patient)
      const parts = [`Patient: ${name}`]
      if (dob) parts.push(`DOB: ${dob}`)
      if (addr) parts.push(`Address: ${addr}`)
      return parts.join(', ')
    }

    case 'allergies': {
      const list = toArray(patient.allergies)
      return list.length > 0 ? `Allergies: ${list.join(', ')}` : 'NKDA'
    }

    case 'current-medications': {
      const list = toArray(patient.currentMedications)
      return list.length > 0 ? `Current medications: ${list.join(', ')}` : 'No current medications recorded'
    }

    case 'chronic-conditions': {
      const list = toArray(patient.chronicConditions)
      return list.length > 0 ? `Chronic conditions: ${list.join(', ')}` : 'No chronic conditions recorded'
    }

    case 'medical-history': {
      const text = patient.medicalHistory?.trim()
      return text ? `Medical history: ${text}` : 'No medical history recorded'
    }

    case 'address': {
      const addr = formatAddress(patient)
      return addr ? `Address: ${addr}` : 'No address recorded'
    }

    case 'contact': {
      const parts: string[] = []
      if (patient.phone) parts.push(patient.phone)
      if (patient.email) parts.push(patient.email)
      return parts.length > 0 ? `Contact: ${parts.join(', ')}` : 'No contact details recorded'
    }

    case 'latest-vitals': {
      if (!vitals || vitals.length === 0) return 'No vitals recorded'

      const sorted = [...vitals].sort(
        (a, b) => new Date(b.recordedAt || 0).getTime() - new Date(a.recordedAt || 0).getTime()
      )
      const latest = sorted[0]
      const dateStr = latest.recordedAt ? formatDate(latest.recordedAt) : ''

      const items: string[] = []
      if (latest.bloodPressure) {
        items.push(`BP ${latest.bloodPressure.systolic}/${latest.bloodPressure.diastolic} mmHg`)
      }
      if (latest.heartRate) {
        items.push(`HR ${latest.heartRate.value} ${latest.heartRate.unit || 'bpm'}`)
      }
      if (latest.temperature) {
        items.push(`Temp ${latest.temperature.value}°C`)
      }
      if (latest.weight) {
        items.push(`Weight ${latest.weight.value} ${latest.weight.unit || 'kg'}`)
      }
      if (latest.height) {
        items.push(`Height ${latest.height.value} ${latest.height.unit || 'cm'}`)
      }
      if (latest.bmi !== undefined) {
        items.push(`BMI ${latest.bmi}`)
      }
      if (latest.oxygenSaturation) {
        items.push(`SpO2 ${latest.oxygenSaturation.value}%`)
      }
      if (latest.respiratoryRate) {
        items.push(`RR ${latest.respiratoryRate.value} /min`)
      }

      if (items.length === 0) return 'No vitals recorded'
      return dateStr ? `Latest vitals (${dateStr}): ${items.join(', ')}` : `Latest vitals: ${items.join(', ')}`
    }

    default:
      return ''
  }
}
